/**
 * The staff survey, defined once.
 *
 * The public form renders from this, the route validates against it, and the
 * dashboard reads and labels answers with it. Three copies of a question set is
 * how a survey ends up collecting an answer nothing displays, or displaying a
 * label that no longer matches what was asked — and unlike most drift, that one
 * is unrecoverable. You cannot go back and ask sixty people what they thought
 * the question meant.
 *
 * Two things are deliberate about the shape.
 *
 * **Sections can carry a reveal, and none currently does.** The mechanism is
 * still here — a panel shown after a section is answered, never before, so it
 * cannot lead the answers beneath it. Both panels are withheld; see
 * WITHHELD_REVEALS for what they said and why they are parked.
 *
 * **Everybody who answers is a teacher.** It went out with five role options
 * and a section that branched on them, because clause 4.3 excludes aides,
 * peripatetic instructors, coaches and chaplains from this agreement and asking
 * them which salary step they are on is asking about a scale they are not paid
 * from. The branch was sound and the premise was not: this goes to secondary
 * teaching staff and nobody else. So the first thing it asked a hundred and
 * twenty people was a question with one true answer, which costs a screen at
 * the point where a form is most likely to be abandoned, and offered four
 * others that would have routed a respondent away from the sections the survey
 * exists to ask about.
 *
 * Everything downstream simplified with it: no eligibility filter on the
 * denominators, because everybody was asked everything.
 */
/*
 * The sources are named and no longer offered.
 *
 * This ended "Both are in the workbench and you can read them yourself", which
 * was written for a representative and read by a hundred and twenty people who
 * are not one. The workbench is behind an invitation and there are six of them;
 * for everybody else that sentence points at a door they cannot open, which is
 * a worse answer than no offer at all — it invites somebody to go and check the
 * figures and then leaves them nothing to check with.
 *
 * Naming the two schedules stays. Anybody can look up Schedule 1A of a public
 * award without help from us, and saying where a number came from is what makes
 * it something other than an assertion.
 *
 * The comment lives here rather than beside the sentence it explains, because
 * everything between these backticks is shown to the reader.
 */
const COMPARISON = `Compared by year of service. The two scales are numbered differently — a four-year-trained teacher starts on Step 5 here and runs to Step 13, while the public scale starts at Step 1 and tops out at Step 7 — so step numbers cannot be lined up.

Your rates from 1 February 2026, against public rates from 9 October 2026, which include the 3% rise at clause 3.1.3:

  year 1    $90,378    $92,882     behind by $2,504    2.8%
  year 3    $97,025   $104,156     behind by $7,131    7.3%
  year 6   $112,471   $124,696     behind by $12,225  10.9%
  year 9   $129,608   $133,422     behind by $3,814    2.9%

The gap is widest in the middle of a career, not at either end — the years a teacher is most likely to be recruited away.

A public teacher reaches the top of their scale in seven years. Here it takes nine, arriving $3,814 below where the public teacher has already been sitting for two years.

Sources: Schedule A of your agreement, and Schedule 1A of the NSW public Award.`;
const TEACHING_TIME = `NSW public secondary. Clause 15.2 requires a timetable of eight 40-minute periods a day — 40 periods a week. Clause 16.1 caps a teacher at 28 of them, plus up to three for sport.

So 12 periods a week, 30% of the timetable, are guaranteed not to be teaching.

Here. Your agreement defines no teaching period, sets no cap on face-to-face teaching, and the word "release" does not appear in it anywhere. Clause 19.3 says ordinary hours during term are "variable". Non-teaching time is whatever is left once the timetable is built.

On a 50-period fortnight at 50 minutes, that is 1,250 minutes a week against the public cap of 1,120 — 11.6% more, which is 130 extra minutes, or between two and three more classes, every week.

Put beside the pay comparison: less money in the middle of a career, more teaching, and no guaranteed non-teaching time at all.`;
/**
 * Written, checked, and deliberately not shown.
 *
 * Two panels used to interrupt the form: the pay comparison after the pay
 * section, and the teaching-time comparison after the time section. Both are
 * withheld at the team's request, on the grounds that a survey which tells you
 * you are underpaid halfway through is measuring its own effect on you by the
 * end of it.
 *
 * That is the right worry and it is not a small one. The reveals sat after the
 * section they related to, so they could not bias the answers directly beneath
 * them — but the form runs to seven sections, and somebody who has just read
 * that they are 10.9% behind answers the four sections after it as a different
 * person. A figure that would have been persuasive coming back from the staff
 * stops being evidence once the staff were shown it first.
 *
 * Kept rather than deleted, and exported rather than commented out, because
 * neither the arithmetic nor the clause references are the reason they were
 * pulled. The tests over this object still hold every figure to the premise it
 * states, so the content cannot rot while it waits. Reinstating either one is
 * putting `reveal: WITHHELD_REVEALS.pay` back on its section.
 *
 * They are also the honest thing to show the staff *after* the survey closes,
 * which is a different act from showing it to them mid-answer.
 */
export const WITHHELD_REVEALS = {
    pay: { title: 'What the agreements actually say', body: COMPARISON },
    time: { title: 'What the agreements say about teaching time', body: TEACHING_TIME },
};
export const SURVEY_SECTIONS = [
    {
        id: 'works',
        title: 'What works here now',
        questions: [
            { id: 'best_things', kind: 'text', prompt: 'What are the two or three best things about working here?', lines: 3 },
            { id: 'would_tell_friend', kind: 'text', prompt: 'If a friend asked whether to apply, what would you tell them?', lines: 3 },
            {
                id: 'values_order',
                kind: 'rank',
                prompt: 'Which of these matter most to you in the workplace?',
                help: 'Drag to order them, most important at the top.',
                choices: [
                    { value: 'colleagues', label: 'Colleagues' },
                    { value: 'purpose', label: 'Purpose and faith' },
                    { value: 'autonomy', label: 'Autonomy in the classroom' },
                    { value: 'leadership', label: 'Leadership' },
                    { value: 'facilities', label: 'Facilities' },
                    { value: 'location', label: 'Location' },
                    { value: 'pay', label: 'Pay' },
                    { value: 'hours', label: 'Hours' },
                    { value: 'holidays', label: 'Holidays' },
                    { value: 'pd', label: 'Professional development' },
                ],
            },
        ],
    },
    {
        id: 'time',
        title: 'Time',
        blurb: 'Your timetable already tells us your periods. What it cannot tell us is the hours around them, which is the part the agreement says nothing about.',
        questions: [
            {
                id: 'hours_prep',
                kind: 'number',
                prompt: 'In a typical week, how many hours do you spend outside timetabled hours on marking, preparation and reporting?',
                unit: 'hours',
                max: 80,
            },
            {
                id: 'hours_out_of_hours',
                kind: 'number',
                prompt: 'In a typical week, how many hours on out-of-hours requirements — camps, co-curricular, events, supervision?',
                unit: 'hours',
                max: 80,
                help: 'Clause 13.1(a) lists all of these as duties a teacher may be required to perform, and attaches no quantum to any of them.',
            },
            {
                id: 'out_of_field_periods',
                kind: 'number',
                prompt: 'Do you teach outside your main subject area? If so, how many periods a fortnight?',
                unit: 'periods',
                max: 60,
                optional: true,
            },
            {
                id: 'more_time_where',
                kind: 'multi',
                prompt: 'If you had more time, where would you spend it?',
                help: 'Choose up to three.',
                max: 3,
                choices: [
                    { value: 'prep', label: 'Lesson preparation' },
                    { value: 'marking', label: 'Marking and feedback' },
                    { value: 'pd', label: 'Professional development' },
                    { value: 'pastoral', label: 'Pastoral care' },
                    { value: 'culture', label: 'Building culture among staff' },
                    { value: 'clubs', label: 'Running clubs or co-curricular' },
                    { value: 'curriculum', label: 'Curriculum development' },
                    { value: 'faculty', label: 'Collaboration with faculty' },
                    { value: 'rest', label: 'Rest' },
                ],
            },
        ],
    },
    {
        id: 'pay',
        title: 'Pay',
        blurb: 'What you think first. We will show you what the agreements actually say at the end of this section.',
        questions: [
            {
                id: 'pay_vs_public',
                kind: 'choice',
                prompt: 'Do you believe your pay is below, above or equal to the public system?',
                choices: [
                    { value: 'below', label: 'Below' },
                    { value: 'equal', label: 'About equal' },
                    { value: 'above', label: 'Above' },
                    { value: 'unknown', label: "Don't know" },
                ],
            },
            {
                id: 'pay_vs_christian',
                kind: 'choice',
                prompt: 'Do you believe your pay is below, above or equal to other private Christian schools in the area?',
                help: 'Schedule H lists around 23 Christian schools on this same agreement — Calderwood, Covenant, Illawarra, Kuyper, Nepean, New Hope and the Pacific group among them. Teachers at all of those are on an identical scale. Christian schools not on this agreement set their own rates.',
                choices: [
                    { value: 'below', label: 'Below' },
                    { value: 'equal', label: 'About equal' },
                    { value: 'above', label: 'Above' },
                    { value: 'unknown', label: "Don't know" },
                ],
            },
            {
                id: 'parity_matters',
                kind: 'choice',
                prompt: 'Is pay parity with either system important to you?',
                choices: [
                    { value: 'public', label: 'Parity with the public system' },
                    { value: 'christian', label: 'Parity with other Christian schools' },
                    { value: 'both', label: 'Both' },
                    { value: 'neither', label: 'Neither' },
                ],
            },
            {
                id: 'pay_justification',
                kind: 'text',
                prompt: 'If you were to ask for more pay, what justification would you give?',
                help: 'If pay is not a factor for you, put NA.',
                lines: 3,
            },
        ],
    },
    {
        id: 'pd',
        title: 'Professional development',
        questions: [
            { id: 'pd_days', kind: 'number', prompt: 'How many days of professional development in the last 12 months?', unit: 'days', max: 60 },
            {
                id: 'pd_chosen',
                kind: 'number',
                prompt: 'Of those, how many did you get to choose?',
                unit: 'days',
                max: 60,
                help: 'Professional development appears once in your agreement, inside the list of duties at clause 13.1(a). There is no entitlement to it, no time allocated, no funding attached and nothing about who chooses.',
            },
            { id: 'pd_wanted', kind: 'text', prompt: 'What professional development would actually help you teach your subject better?', lines: 3 },
        ],
    },
    {
        id: 'leave',
        title: 'Leave',
        questions: [
            {
                id: 'leave_denied',
                kind: 'choice',
                prompt: 'Have you needed leave you could not access?',
                choices: [
                    { value: 'no', label: 'No' },
                    { value: 'yes', label: 'Yes' },
                ],
                help: 'Your agreement gives fifteen days paid personal or carer’s leave a year at clause 24.1(a), and it accumulates indefinitely with no maximum under clause 24.4.',
            },
            { id: 'leave_denied_what', kind: 'text', prompt: 'If yes, what kind?', lines: 2, optional: true },
            { id: 'leave_change', kind: 'text', prompt: "Anything about leave here that you'd protect as-is, change or extend?", lines: 3 },
        ],
    },
    {
        id: 'staying',
        title: 'Staying and leaving',
        blurb: 'Nothing in this section is linked to anything else you have answered, and no question here asks your faculty, your years of service or your step. Skip any of it.',
        questions: [
            {
                id: 'looked_elsewhere',
                kind: 'choice',
                prompt: 'In the last six months, have you looked at roles at other schools?',
                optional: true,
                choices: [
                    { value: 'no', label: 'No' },
                    { value: 'idly', label: 'Looked idly' },
                    { value: 'applied', label: 'Applied for something' },
                    { value: 'interviewed', label: 'Interviewed somewhere' },
                ],
            },
            {
                id: 'move_drivers',
                kind: 'multi',
                prompt: 'If you were considering a move, what would be driving it?',
                optional: true,
                choices: [
                    { value: 'pay', label: 'Pay' },
                    { value: 'load', label: 'Teaching load' },
                    { value: 'release', label: 'Release time' },
                    { value: 'out_of_hours', label: 'Out-of-hours expectations' },
                    { value: 'leadership', label: 'Leadership' },
                    { value: 'progression', label: 'Progression' },
                    { value: 'commute', label: 'Commute' },
                    { value: 'other', label: 'Something else' },
                ],
            },
            { id: 'would_stay', kind: 'text', prompt: 'What would make you stay for the next five years?', lines: 3, optional: true },
        ],
    },
    {
        id: 'ideas',
        title: 'Ideas',
        questions: [
            {
                id: 'cheap_ideas',
                kind: 'text',
                prompt: 'Have you seen something at another school — cheap or free — that made a real difference?',
                help: 'For example: a marking-free week, a duty roster that rotates, timetabled faculty planning, a no-email window after hours.',
                lines: 4,
            },
            { id: 'anything_else', kind: 'text', prompt: 'Anything else?', lines: 4, optional: true },
        ],
    },
];
/**
 * Every question id anybody could legitimately answer.
 *
 * This used to be `questionIdsFor(role)`, beside a `sectionsFor(role)` that
 * filtered on a section's `onlyFor`. Both are gone with the role branch: a
 * selector that takes an argument it no longer consults reads as though a
 * decision is being made somewhere, and the next person to touch the
 * denominators would go looking for one.
 */
export function allQuestionIds() {
    return new Set(SURVEY_SECTIONS.flatMap((s) => s.questions.map((q) => q.id)));
}
/**
 * The address to give a hundred and twenty people.
 *
 * The dashboard used to print `#/survey/staff-2026`, which is not a link. It is
 * the tail of one, and a representative pasting it into a staff email sends
 * something nobody can open — the failure being that it looks close enough to
 * right that it would go out unchecked.
 *
 * Built here rather than in the component so the shape can be asserted without
 * a browser, since it is the part that is wrong when this is wrong. Trailing
 * slashes are stripped because `origin` is normally bare but a caller passing
 * `href` should not produce a double slash, and the hash is not optional: the
 * application is hash-routed, so /survey/staff-2026 without it reaches the
 * server, which knows nothing about that path.
 */
export function surveyLink(origin, slug) {
    return `${origin.replace(/\/+$/, '')}/#/survey/${slug}`;
}
//# sourceMappingURL=survey.js.map